import { Callout, DirectionalHint, FontWeights, Icon, mergeStyleSets, Text } from '@fluentui/react';
import { useBoolean, useId } from '@fluentui/react-hooks';
import { PrimaryButton } from '@fluentui/react/lib/Button';
import { DateTime } from 'luxon';
import React from 'react';
import { genkey } from '../../common/constants';
import { Partido, Planilla } from '../../system/SystemClient';
import ClubImageCircle from '../Common/ClubImageCircle';

const LIVE_REC_CSS = `
.po-live-rec-dot{
  width:14px; height:14px; border-radius:50%;
  background:#ef4444;
  box-shadow:0 0 0 0 rgba(239,68,68,0.55);
  display:inline-block;
  animation:po-live-pulse 1200ms ease-out infinite;
  transform:translateZ(0);
}
@keyframes po-live-pulse{
  0%   { box-shadow:0 0 0 0 rgba(239,68,68,0.55); transform:scale(1); }
  70%  { box-shadow:0 0 0 10px rgba(239,68,68,0);  transform:scale(1.09); }
  100% { box-shadow:0 0 0 0 rgba(239,68,68,0);    transform:scale(1); }
}
@media (prefers-reduced-motion: reduce){
  .po-live-rec-dot{ animation:none; }
}
.po-sr-only{
  position:absolute; width:1px; height:1px; padding:0; margin:-1px;
  overflow:hidden; clip:rect(0,0,0,0); white-space:nowrap; border:0;
}
`;

const LiveRec: React.FC<{ mt?: number; mr?: number; size?: number }> = ({
  mt = 2, // margen superior sutil
  mr = 8, // separación a la derecha
  size = 14,
}) => (
  <span
    style={{
      display: 'inline-flex',
      alignItems: 'center',
      verticalAlign: 'middle',
      marginTop: mt,
      marginRight: mr,
    }}
  >
    <style>{LIVE_REC_CSS}</style>
    {/* Texto "En vivo" */}
    <span
      style={{
        fontSize: 12,
        fontWeight: 600,
        color: '#ef4444',
        marginRight: 8,
      }}
    >
      En vivo
    </span>
    <span
      className="po-live-rec-dot"
      style={{ width: size, height: size }}
      aria-label="Partido en vivo"
      role="status"
      title="En vivo"
    />
    <span className="po-sr-only">En vivo</span>
  </span>
);

export const PartidoFixture = ({
  partido,
  highlightClubId,
}: {
  partido: Partido;
  highlightClubId?: string;
}) => {
  const [isCalloutPlanillaVisibleDesktop, { toggle: toggleIsCalloutVisibleDesktop }] = useBoolean(false);
  const buttonIdDesktop = useId(genkey());
  const labelIdDesktop = useId(genkey());
  const descriptionIdDesktop = useId(genkey());

  const [
    isCalloutPendienteConfirmacionVisibleDesktop,
    { toggle: toggleIsCalloutPendienteConfirmacionVisibleDesktop },
  ] = useBoolean(false);
  const buttonIdPendienteConfirmacionDesktop = useId(genkey());
  const labelIdPendienteConfirmacionDesktop = useId(genkey());
  const descriptionIdPendienteConfirmacionDesktop = useId(genkey());

  const [isCalloutCampoJuegoVisibleDesktop, { toggle: toggleIsCalloutCampoJuegoVisibleDesktop }] =
    useBoolean(false);
  const labelIdCampoJuegoDesktop = useId(genkey());
  const descriptionIdCampoJuegoDesktop = useId(genkey());
  const buttonIdCampoJuegoDesktop = useId(genkey());

  const [isCalloutArbitrosVisibleDesktop, { toggle: toggleIsCalloutArbitrosVisibleDesktop }] =
    useBoolean(false);
  const labelIdArbitrosDesktop = useId(genkey());
  const descriptionIdArbitrosDesktop = useId(genkey());
  const buttonIdArbitrosDesktop = useId(genkey());

  const [isCalloutPlanillaVisibleMobile, { toggle: toggleIsCalloutVisibleMobile }] = useBoolean(false);
  const buttonIdMobile = useId(genkey());
  const labelIdMobile = useId(genkey());
  const descriptionIdMobile = useId(genkey());

  const [
    isCalloutPendienteConfirmacionMobile,
    { toggle: toggleIsCalloutPendienteConfirmacionVisibleMobile },
  ] = useBoolean(false);
  const buttonIdPendienteConfirmacionMobile = useId(genkey());
  const labelIdPendienteConfirmacionMobile = useId(genkey());
  const descriptionIdPendienteConfirmacionMobile = useId(genkey());

  const [isCalloutCampoJuegoVisibleMobile, { toggle: toggleIsCalloutCampoJuegoVisibleMobile }] =
    useBoolean(false);
  const labelIdCampoJuegoMobile = useId(genkey());
  const descriptionIdCampoJuegoMobile = useId(genkey());
  const buttonIdCampoJuegoMobile = useId(genkey());

  const winnerClass = (golesPropios: number, golesAComparar: number) => {
    return golesPropios > golesAComparar;
  };

  const openCalloutOrOpenVideo = (planillas: Planilla[], openCallout: () => void) => {
    const cantTransmisiones = planillas.filter((planilla) => !!planilla.url_transmision).length;
    if (cantTransmisiones === 1) {
      const unicaTransmision = planillas
        .filter((planilla) => !!planilla.url_transmision)
        .map((planilla) => planilla.url_transmision);
      window.open(unicaTransmision[0], '_blank');
    } else {
      openCallout();
    }
  };

  const openCalloutOrOpenPDF = (planillas: Planilla[], openCallout: () => void) => {
    const cantPDFs = planillas.filter((planilla) => !!planilla.pdf).length;
    if (cantPDFs === 1) {
      const unicoPDF = planillas.filter((planilla) => !!planilla.pdf).map((planilla) => planilla.pdf);
      console.log('link > ', unicoPDF[0]);
      window.open(unicoPDF[0], '_blank');
    } else {
      openCallout();
    }
  };

  const shouldHighlight = (id: string) => {
    return !!id && !!highlightClubId && +id === +highlightClubId;
  };

  const openMap = (campoJuegoCoords: string) => {
    const coords = campoJuegoCoords.split(';');
    const lat = coords[0];
    const lng = coords[1];
    window.open(`https://www.google.com/maps/search/?api=1&query=${lat},${lng}`, '_blank');
  };

  const openMapWithDireccion = (campoJuegoDireccion: string) => {
    window.open(
      `https://www.google.com/maps/search/?api=1&query=${encodeURI(campoJuegoDireccion)}`,
      '_blank',
    );
  };

  const golesLocalGlobal = +partido.golesLocal + +partido.golesPenalLocal;
  const golesVisitanteGlobal = +partido.golesVisitante + +partido.golesPenalVisitante;
  const tuvoPenales = +partido.golesPenalLocal > 0 || +partido.golesPenalVisitante > 0;

  return (
    <div className="ms-Grid-row text-center" style={{ marginTop: 5, marginBottom: 5 }} key={genkey()}>
      {/* =====================================================================
                              VISTA DESKTOP
        =====================================================================*/}
      <div id="viewDesktop" className="ms-hiddenSm ">
        <div className="ms-Grid-row">
          <div className="ms-Grid-col ms-sm12 mt-1">
            <div className="ms-Grid-col ms-sm2  text-overflow " style={{ marginTop: 9, width: '14.66%' }}>
              <Text
                style={{
                  fontSize: 13,
                  float: 'left',
                  marginLeft: 10,
                }}
                className="mb-1"
                variant="medium"
              >
                {!partido.isGhostMatch && (
                  <>
                    <Icon
                      iconName="Calendar"
                      className="text-icon"
                      style={{ fontSize: '16px !important' }}
                    ></Icon>
                    {DateTime.fromFormat(partido.horario, 'yyyy/LL/dd HH:mm:ss')
                      .setLocale('es')
                      .toFormat('ccc dd LLL HH:mm')}
                  </>
                )}
              </Text>
            </div>

            <div className="ms-Grid-col ms-sm3 text-overflow" style={{ minHeight: 40, width: '30%' }}>
              <div
                className={
                  'ms-Grid-col ms-sm10 text-overflow ' +
                  (shouldHighlight(partido.idClubLocal) ? 'highlight-club highlight-club--mount' : '')
                }
                style={{ minHeight: 40, padding: '4px 8px 6px 10px' }} // padding para que no pegue al borde
              >
                <Text
                  className={'mb-1 nombre-equipo-fixture nombre-local team-name'}
                  variant="medium"
                  style={{ fontWeight: shouldHighlight(partido.idClubLocal) ? 600 : 500 }}
                >
                  {partido.isGhostMatch && !partido.nombreLocal ? 'LIBRE' : partido.nombreLocal}
                  {!partido?.presenteLocal && partido.nombreLocal && (
                    <span style={{ color: 'red' }}>(N.P.)</span>
                  )}
                </Text>
              </div>

              <div className="ms-Grid-col ms-sm2" style={{ minHeight: 40 }}>
                <div>
                  <ClubImageCircle
                    escudo={partido.escudoImagePathLocal}
                    width={35}
                    height={35}
                    right
                  ></ClubImageCircle>
                </div>
              </div>
            </div>
            <div
              className={`ms-Grid-col ms-sm2 ${partido.playing ? 'cool-link' : ''}`}
              style={{ marginTop: 5, width: '11%', minWidth: 90 }}
            >
              {!partido.isGhostMatch && (partido.played || partido.playing) ? (
                <div
                  style={{ fontWeight: 600, color: 'black', fontSize: 18, display: 'inline-flex' }}
                  title={partido.pendingConfirmation ? 'Partido pendiente de confirmación' : ''}
                >
                  <div
                    className={
                      partido.pendingConfirmation
                        ? 'pending-confirmation-local score'
                        : winnerClass(+golesLocalGlobal, +golesVisitanteGlobal)
                          ? 'winner score'
                          : 'loser score'
                    }
                  >
                    {partido.golesLocal}{' '}
                    {tuvoPenales && <span style={{ fontSize: 12 }}>({partido.golesPenalLocal})</span>}
                  </div>{' '}
                  <div
                    className={
                      partido.pendingConfirmation
                        ? 'pending-confirmation-visitante score'
                        : winnerClass(+golesVisitanteGlobal, +golesLocalGlobal)
                          ? 'winner score'
                          : 'loser score'
                    }
                  >
                    {partido.golesVisitante}{' '}
                    {tuvoPenales && <span style={{ fontSize: 12 }}>({partido.golesPenalVisitante})</span>}
                  </div>
                </div>
              ) : (
                <span>Vs</span>
              )}
            </div>
            <div className="ms-Grid-col ms-sm3 text-overflow" style={{ minHeight: 40, width: '30%' }}>
              <div className="ms-Grid-col ms-sm2" style={{ minHeight: 40 }}>
                <div>
                  <ClubImageCircle
                    escudo={partido.escudoImagePathVisitante}
                    width={35}
                    height={35}
                  ></ClubImageCircle>
                </div>
              </div>

              <div
                className={
                  'ms-Grid-col ms-sm10 text-overflow ' +
                  (shouldHighlight(partido.idClubVisitante) ? 'highlight-club highlight-club--mount' : '')
                }
                style={{ minHeight: 30, padding: '4px 8px 6px 10px' }}
              >
                <Text
                  className="mb-1 nombre-equipo-fixture nombre-visitante team-name"
                  variant="medium"
                  style={{ fontWeight: shouldHighlight(partido.idClubVisitante) ? 600 : 500 }}
                >
                  {partido.isGhostMatch && !partido.nombreVisitante ? 'LIBRE' : partido.nombreVisitante}
                  {!partido?.presenteVisitante && partido.nombreVisitante && (
                    <span style={{ color: 'red' }}>(N.P.)</span>
                  )}
                </Text>
              </div>
            </div>
            <div
              className="ms-Grid-col ms-sm2  text-overflow "
              style={{ marginTop: 9, width: '13.66%', textAlign: 'left' }}
            >
              {!partido.isGhostMatch && (
                <div className="ms-Grid-col ms-sm12  text-overflow " style={{ textAlign: 'left' }}>
                  <PrimaryButton
                    iconProps={{ iconName: 'PublishCourse' }}
                    title="Ver arbitros"
                    id={buttonIdArbitrosDesktop}
                    className="float-right ml-2 "
                    onClick={toggleIsCalloutArbitrosVisibleDesktop}
                    disabled={!!!partido.arbitros}
                    styles={{
                      root: {
                        border: !!!partido.arbitros ? undefined : '1px solid #00b2ff',
                        background: !!!partido.arbitros ? undefined : '#00b2ff',
                        borderRadius: 25,
                        padding: 0,
                        minWidth: 25,
                        height: 27,
                      },
                    }}
                  ></PrimaryButton>
                  <PrimaryButton
                    iconProps={{ iconName: 'POI' }}
                    title="Ver estadio"
                    id={buttonIdCampoJuegoDesktop}
                    className="float-right ml-2 "
                    onClick={toggleIsCalloutCampoJuegoVisibleDesktop}
                    styles={{
                      root: {
                        border: '1px solid #009454',
                        background: '#009454',
                        borderRadius: 25,
                        padding: 0,
                        minWidth: 25,
                        height: 27,
                      },
                    }}
                  ></PrimaryButton>

                  {partido.pendingConfirmation ? (
                    <PrimaryButton
                      iconProps={{ iconName: 'AccountActivity' }}
                      id={buttonIdPendienteConfirmacionDesktop}
                      onClick={() => toggleIsCalloutPendienteConfirmacionVisibleDesktop()}
                      styles={{
                        root: {
                          border: '1px solid #f79c25',
                          background: '#f79c25',
                          borderRadius: 25,
                          padding: 0,
                          minWidth: 25,
                          height: 27,
                        },
                      }}
                      className="float-right ml-2 "
                    ></PrimaryButton>
                  ) : (
                    <PrimaryButton
                      iconProps={{ iconName: 'AccountActivity' }}
                      id={buttonIdDesktop}
                      onClick={() => openCalloutOrOpenPDF(partido.planillas, toggleIsCalloutVisibleDesktop)}
                      styles={{ root: { borderRadius: 25, padding: 0, minWidth: 25, height: 27 } }}
                      title="Ver planilla"
                      className="float-right ml-2 "
                      disabled={partido.planillas.filter((planilla) => planilla.pdf).length === 0}
                    ></PrimaryButton>
                  )}

                  {partido.planillas.some((planilla) => planilla.url_transmision) && (
                    <PrimaryButton
                      iconProps={{ iconName: 'Video' }}
                      className="float-right "
                      title="Ver transmisión"
                      id={buttonIdDesktop}
                      onClick={() => openCalloutOrOpenVideo(partido.planillas, toggleIsCalloutVisibleDesktop)}
                      styles={{
                        root: {
                          border: '1px solid #f79c25',
                          background: '#f79c25',
                          borderRadius: 25,
                          padding: 0,
                          minWidth: 25,
                          height: 27,
                        },
                      }}
                    ></PrimaryButton>
                  )}

                  <>
                    {isCalloutPlanillaVisibleDesktop && (
                      <Callout
                        ariaLabelledBy={labelIdDesktop}
                        ariaDescribedBy={descriptionIdDesktop}
                        role="dialog"
                        gapSpace={0}
                        target={`#${buttonIdDesktop}`}
                        onDismiss={toggleIsCalloutVisibleDesktop}
                        setInitialFocus
                        className={styles.callout}
                        directionalHint={DirectionalHint.leftCenter}
                      >
                        <PlanillasCallout partido={partido} labelId={labelIdDesktop}></PlanillasCallout>
                      </Callout>
                    )}
                  </>

                  <>
                    {isCalloutPendienteConfirmacionVisibleDesktop && (
                      <Callout
                        ariaLabelledBy={labelIdPendienteConfirmacionDesktop}
                        ariaDescribedBy={descriptionIdPendienteConfirmacionDesktop}
                        role="dialog"
                        gapSpace={0}
                        target={`#${buttonIdPendienteConfirmacionDesktop}`}
                        onDismiss={toggleIsCalloutPendienteConfirmacionVisibleDesktop}
                        setInitialFocus
                        className={styles.callout}
                        directionalHint={DirectionalHint.leftCenter}
                      >
                        <PendienteConfirmacionCallout></PendienteConfirmacionCallout>
                      </Callout>
                    )}
                  </>

                  <>
                    {isCalloutCampoJuegoVisibleDesktop && (
                      <Callout
                        ariaLabelledBy={labelIdCampoJuegoDesktop}
                        ariaDescribedBy={descriptionIdCampoJuegoDesktop}
                        role="dialog"
                        gapSpace={0}
                        target={`#${buttonIdCampoJuegoDesktop}`}
                        onDismiss={toggleIsCalloutCampoJuegoVisibleDesktop}
                        setInitialFocus
                        className={styles.callout}
                        directionalHint={DirectionalHint.leftCenter}
                      >
                        <span style={{ margin: 10, fontSize: 14, fontWeight: 500 }}>
                          {partido.campoJuegoNombre}
                        </span>
                        {partido.campoJuegoCoords ? (
                          <span
                            className="link-styles"
                            onClick={() => openMap(partido.campoJuegoCoords)}
                            style={{ marginLeft: 10, fontSize: 12, fontWeight: 500, display: 'block' }}
                          >
                            Ver dirección
                          </span>
                        ) : (
                          <div>
                            {partido.campoJuegoDireccion && (
                              <span
                                className="link-styles"
                                onClick={() => openMapWithDireccion(partido.campoJuegoDireccion)}
                                style={{ marginLeft: 10, fontSize: 12, fontWeight: 500, display: 'block' }}
                              >
                                Ver dirección
                              </span>
                            )}
                          </div>
                        )}
                      </Callout>
                    )}
                  </>

                  <>
                    {isCalloutArbitrosVisibleDesktop && (
                      <Callout
                        ariaLabelledBy={labelIdArbitrosDesktop}
                        ariaDescribedBy={descriptionIdArbitrosDesktop}
                        role="dialog"
                        gapSpace={0}
                        target={`#${buttonIdArbitrosDesktop}`}
                        onDismiss={toggleIsCalloutArbitrosVisibleDesktop}
                        setInitialFocus
                        className={styles.callout}
                        directionalHint={DirectionalHint.leftCenter}
                      >
                        <div>
                          <div>
                            <span style={{ margin: 10, fontSize: 14, fontWeight: 500 }}>Arbitros</span>
                          </div>
                          <div>
                            <span style={{ margin: 10, fontSize: 14, fontWeight: 400 }}>
                              {partido.arbitros ? partido.arbitros : 'Aun no designados'}
                            </span>
                          </div>
                        </div>
                      </Callout>
                    )}
                  </>
                </div>
              )}
            </div>
          </div>
        </div>
      </div>
      {/* =====================================================================
                              VISTA MOBILE
        =====================================================================*/}

      <div
        id="viewMobile"
        className="ms-hiddenMdUp mt-1"
        style={{ borderTop: '1px solid #e6e6e6', marginTop: 5 }}
      >
        <div className="ms-Grid-col ms-sm5 text-overflow text-left" style={{ marginTop: 5 }}>
          <Text className="mb-1" variant="small">
            {!partido.isGhostMatch && (
              <>
                <Icon iconName="Calendar" style={{ marginLeft: 5 }} className="text-icon-small" />
                {DateTime.fromFormat(partido.horario, 'yyyy/LL/dd HH:mm:ss')
                  .setLocale('es')
                  .toFormat('ccc dd LLL HH:mm')}
              </>
            )}
          </Text>
        </div>

        <div className="ms-Grid-col ms-sm7 text-overflow text-left" style={{ marginTop: 5 }}>
          <PrimaryButton
            iconProps={{ iconName: 'POI' }}
            title="Ver estadio"
            id={buttonIdCampoJuegoMobile}
            className="float-right"
            onClick={toggleIsCalloutCampoJuegoVisibleMobile}
            styles={{
              root: {
                border: '1px solid #009454',
                background: '#009454',
                borderRadius: 25,
                padding: 0,
                minWidth: 23,
                height: 26,
                marginLeft: 5,
              },
            }}
          ></PrimaryButton>

          {partido.pendingConfirmation ? (
            <PrimaryButton
              iconProps={{ iconName: 'AccountActivity' }}
              id={buttonIdPendienteConfirmacionMobile}
              onClick={() => toggleIsCalloutPendienteConfirmacionVisibleMobile()}
              styles={{
                root: {
                  borderRadius: 25,
                  padding: 0,
                  minWidth: 23,
                  height: 26,
                  marginLeft: 5,
                  border: '1px solid #f79c25',
                  background: '#f79c25',
                },
              }}
              className="float-right"
            ></PrimaryButton>
          ) : (
            <PrimaryButton
              iconProps={{ iconName: 'AccountActivity' }}
              id={buttonIdMobile}
              onClick={() => openCalloutOrOpenPDF(partido.planillas, toggleIsCalloutVisibleMobile)}
              styles={{ root: { borderRadius: 25, padding: 0, minWidth: 23, height: 26, marginLeft: 5 } }}
              title="Ver planilla"
              className="float-right"
              disabled={partido.planillas.filter((planilla) => planilla.pdf).length === 0}
            ></PrimaryButton>
          )}

          {partido.planillas.some((planilla) => planilla.url_transmision) && (
            <PrimaryButton
              iconProps={{ iconName: 'Video' }}
              className="float-right "
              title="Ver transmisión"
              id={buttonIdMobile + 'M'}
              onClick={() => openCalloutOrOpenVideo(partido.planillas, toggleIsCalloutVisibleMobile)}
              styles={{
                root: {
                  border: '1px solid #f79c25',
                  background: '#f79c25',
                  borderRadius: 25,
                  padding: 0,
                  minWidth: 23,
                  height: 26,
                },
              }}
            ></PrimaryButton>
          )}
          <div style={{ float: 'right' }}>{partido.playing ? <LiveRec mt={3} mr={5} size={12} /> : null}</div>

          <>
            {isCalloutPlanillaVisibleMobile && (
              <Callout
                ariaLabelledBy={labelIdMobile}
                ariaDescribedBy={descriptionIdMobile}
                role="dialog"
                gapSpace={0}
                target={`#${buttonIdMobile}`}
                onDismiss={toggleIsCalloutVisibleMobile}
                setInitialFocus
                className={styles.callout}
                directionalHint={DirectionalHint.leftCenter}
              >
                <PlanillasCallout partido={partido} labelId={labelIdMobile}></PlanillasCallout>
              </Callout>
            )}
          </>

          <>
            {isCalloutPendienteConfirmacionMobile && (
              <Callout
                ariaLabelledBy={labelIdPendienteConfirmacionMobile}
                ariaDescribedBy={descriptionIdPendienteConfirmacionMobile}
                role="dialog"
                gapSpace={0}
                target={`#${buttonIdPendienteConfirmacionMobile}`}
                onDismiss={toggleIsCalloutPendienteConfirmacionVisibleMobile}
                setInitialFocus
                className={styles.callout}
                directionalHint={DirectionalHint.leftCenter}
              >
                <PendienteConfirmacionCallout></PendienteConfirmacionCallout>
              </Callout>
            )}
          </>

          <>
            {isCalloutCampoJuegoVisibleMobile && (
              <Callout
                ariaLabelledBy={labelIdCampoJuegoMobile}
                ariaDescribedBy={descriptionIdCampoJuegoMobile}
                role="dialog"
                gapSpace={0}
                target={`#${buttonIdCampoJuegoMobile}`}
                onDismiss={toggleIsCalloutCampoJuegoVisibleMobile}
                setInitialFocus
                className={styles.callout}
                directionalHint={DirectionalHint.leftCenter}
              >
                <span style={{ margin: 10, fontSize: 14, fontWeight: 500 }}>{partido.campoJuegoNombre}</span>
                {partido.campoJuegoCoords ? (
                  <span
                    className="link-styles"
                    onClick={() => openMap(partido.campoJuegoCoords)}
                    style={{ marginLeft: 10, fontSize: 12, fontWeight: 500, display: 'block' }}
                  >
                    Ver dirección
                  </span>
                ) : (
                  <div>
                    {partido.campoJuegoDireccion && (
                      <span
                        className="link-styles"
                        onClick={() => openMapWithDireccion(partido.campoJuegoDireccion)}
                        style={{ marginLeft: 10, fontSize: 12, fontWeight: 500, display: 'block' }}
                      >
                        Ver dirección
                      </span>
                    )}
                  </div>
                )}
              </Callout>
            )}
          </>
        </div>

        <div className="ms-Grid-col ms-sm12" style={{ minHeight: 30 }}>
          <TeamMobile
            escudo={partido.escudoImagePathLocal}
            nombre={partido.nombreLocal}
            goles={partido.played || partido.playing ? partido.golesLocal : '-'}
            winner={partido.golesLocal > partido.golesVisitante}
            stylesHighlight={getStylesBy(partido.idClubLocal)}
            classNameHighlight={getClassBy(partido.idClubLocal)}
          ></TeamMobile>
          <TeamMobile
            escudo={partido.escudoImagePathVisitante}
            nombre={partido.nombreVisitante}
            goles={partido.played || partido.playing ? partido.golesVisitante : '-'}
            winner={partido.golesVisitante > partido.golesLocal}
            stylesHighlight={getStylesBy(partido.idClubVisitante)}
            classNameHighlight={getClassBy(partido.idClubVisitante)}
          ></TeamMobile>
        </div>
      </div>
    </div>
  );

  function getStylesBy(id: string): React.CSSProperties | undefined {
    const is = shouldHighlight(id);
    return {
      fontWeight: is ? 600 : 500,
      color: 'inherit', // mantené el color tipográfico; nada en rojo
    };
  }

  function getClassBy(id: string): string {
    const is = shouldHighlight(id);
    // aplicá el mount sólo la primera vez que lo marcás (opcional)
    return is ? 'highlight-club highlight-club--mount' : '';
  }
};

const TeamMobile = (props: {
  escudo: string;
  nombre: string;
  goles: string;
  winner: boolean;
  stylesHighlight: React.CSSProperties | undefined;
  classNameHighlight?: string; // ← nuevo
}) => {
  const ROW_H = 34; // altura consistente en mobile

  return (
    <div className="ms-Grid-row mt-1" style={{ minHeight: ROW_H }}>
      <div className="ms-Grid-col ms-sm1 text-overflow" style={{ minHeight: ROW_H }}>
        <ClubImageCircle escudo={props.escudo} width={25} height={25} />
      </div>

      <div className="ms-Grid-col ms-sm9 text-overflow" style={{ minHeight: ROW_H }}>
        <div
          className={props.classNameHighlight /* 'highlight-club ...' o '' */}
          style={{
            display: 'flex',
            alignItems: 'center',
            width: '100%',
            height: ROW_H - 6,
            padding: '0 8px',
            borderRadius: 6,
            boxSizing: 'border-box',
            minWidth: 0,
          }}
        >
          <Text
            className={`mb-1 text-overflow text-left team-name ${
              props.winner ? 'nombre-equipo-fixture-mobile-winner' : 'nombre-equipo-fixture-mobile'
            }`}
            variant="medium"
            style={{
              ...props.stylesHighlight,
              lineHeight: 1.2,
              whiteSpace: 'nowrap',
              overflow: 'hidden',
              textOverflow: 'ellipsis',
              width: '100%',
            }}
            title={props.nombre}
          >
            {props.nombre}
          </Text>
        </div>
      </div>

      <div className="ms-Grid-col ms-sm2 text-overflow" style={{ minHeight: ROW_H }}>
        <div className={props.winner ? 'winner-mobile' : 'loser-mobile'}>{props.goles}</div>
      </div>
    </div>
  );
};

const styles = mergeStyleSets({
  button: {
    width: 130,
  },
  callout: {
    width: 420,
    maxWidth: '90%',
    padding: '6px 10px',
  },
  title: {
    marginBottom: 12,
    fontWeight: FontWeights.semilight,
  },
  link: {
    display: 'block',
    marginTop: 20,
  },
});

const PlanillasCallout = ({ partido, labelId }: { partido: Partido; labelId: any }) => {
  return (
    <div className="ms-Grid" dir="ltr">
      <div className="ms-Grid-row">
        <div className="ms-Grid-col ms-sm12">
          <Text block variant="mediumPlus" style={{ fontWeight: 600 }} className="text-center" id={labelId}>
            Elegí la planilla que quieras ver
          </Text>
        </div>
        <div className="ms-Grid-col ms-sm12">
          <hr></hr>
        </div>
      </div>
      {partido.planillas.map((planilla) => {
        return (
          <div className="ms-Grid-row mt-1">
            <div className="ms-Grid-col ms-sm1" style={{ paddingRight: 0 }}>
              <ClubImageCircle
                escudo={partido.escudoImagePathLocal}
                width={25}
                height={25}
                right
              ></ClubImageCircle>
            </div>

            <div
              className="ms-Grid-col ms-sm3 text-center"
              style={{
                marginTop: 3,
                fontWeight: 600,
                paddingLeft: 0,
                paddingRight: 0,
              }}
            >
              {planilla.local.goles} - {planilla.visitante.goles}
            </div>
            <div className="ms-Grid-col ms-sm1" style={{ paddingLeft: 0 }}>
              <ClubImageCircle
                escudo={partido.escudoImagePathVisitante}
                width={25}
                height={25}
              ></ClubImageCircle>
            </div>

            <div className="ms-Grid-col ms-sm7 text-center">
              <PrimaryButton
                disabled={!!!planilla.pdf}
                onClick={() => {
                  window.open(planilla.pdf, '_blank');
                }}
                iconProps={{ iconName: planilla.pdf ? 'AccountActivity' : 'Cancel' }}
                title="Ver planilla"
                styles={{
                  root: {
                    borderRadius: 25,
                    padding: 0,
                    minWidth: 25,
                    height: 27,
                  },
                }}
              ></PrimaryButton>
              <PrimaryButton
                disabled={!!!planilla.url_transmision}
                onClick={() => {
                  window.open(planilla.url_transmision, '_blank');
                }}
                iconProps={{ iconName: planilla.url_transmision ? 'Video' : 'Cancel' }}
                className="ml-2"
                title="Ver transmisión"
                styles={{
                  root: {
                    borderRadius: 25,
                    padding: 0,
                    minWidth: 25,
                    height: 27,
                  },
                }}
              ></PrimaryButton>
            </div>
          </div>
        );
      })}
    </div>
  );
};

const PendienteConfirmacionCallout = () => {
  return (
    <div className="ms-Grid" dir="ltr">
      <div className="ms-Grid-row">
        <div className="ms-Grid-col ms-sm12">
          <Text block variant="mediumPlus" style={{ fontWeight: 600 }} className="text-center">
            El partido esta pendiente de confirmación
          </Text>
        </div>
      </div>
    </div>
  );
};
